/**
 * Estado do mar de uma boia, derivado do histórico `imu`.
 *
 * O grau e o rótulo seguem a escala de Douglas (0 espelhado … 9 excecional),
 * que é o que se quer mostrar a quem não lê "pitch RMS": uma palavra e uma
 * altura em metros.
 *
 * Duas proveniências, nunca misturadas:
 *  - **medida** — firmware novo calcula altura significativa, período de pico
 *    e swell sobre a série de 10 Hz, que nunca sai do nó;
 *  - **estimada** — firmware antigo só manda o RMS da aceleração vertical.
 *    Dá para estimar a altura (Hs = 4·σ·(T/2π)², relação de águas profundas),
 *    mas leva o período ao quadrado — e o período não se mede daqui. Assume-se
 *    o típico da costa noroeste e diz-se por extenso que é estimativa.
 *
 * Janelas de 60 s são curtas para estatística de mar (as boias ondógrafo usam
 * 10–20 min): o valor "atual" é a mediana das últimas janelas, não a última.
 */

import type { ImuLatest, ImuPoint } from "../types";

export interface EstadoMar {
  /** Altura significativa, m. Mediana das últimas janelas. */
  alturaM: number | null;
  /** Período do pico espectral, s. Só com fonte medida. */
  periodoS: number | null;
  /** Ondulação de fundo (período > 8 s), m. Só com fonte medida. */
  swellM: number | null;
  /** Grau de Douglas 0–9 correspondente à altura. */
  grau: number | null;
  rotulo: string | null;
  fonte: "medida" | "estimada" | null;
}

export const VAZIO: EstadoMar = {
  alturaM: null,
  periodoS: null,
  swellM: null,
  grau: null,
  rotulo: null,
  fonte: null,
};

/**
 * Limiares de altura (m) dos graus de Douglas 1…9. O limiar de 2 cm no grau 1
 * não é da escala (que põe o grau 1 até 10 cm): abaixo disso a diferença para
 * o espelhado é ruído do sensor, não mar.
 */
const DOUGLAS_M = [0.02, 0.1, 0.5, 1.25, 2.5, 4, 6, 9, 14];

export const ROTULO_MAR = [
  "Espelhado",
  "Calmo",
  "Pouco agitado",
  "Ligeiramente agitado",
  "Moderado",
  "Agitado",
  "Muito agitado",
  "Alteroso",
  "Tempestuoso",
  "Excecional",
] as const;

/** Período assumido na estimativa: ondulação típica na costa NW portuguesa. */
export const PERIODO_TIPICO_S = 8;

/**
 * RMS da aceleração vertical do BNO055 **parado** (banco, teste de resistência
 * de 2026-08: ~0,029 m/s²). O firmware novo separa ruído de onda no espetro;
 * na estimativa só dá para subtrair em quadratura — sem isto, uma boia em
 * águas paradas aparecia com 0,2 m de mar inventado pelo próprio sensor.
 */
const RUIDO_SENSOR_MS2 = 0.03;

/** Janelas recentes na mediana do "agora" (~10 min de mar). */
const JANELAS_MEDIANA = 10;

export function grauDouglas(alturaM: number): number {
  let grau = 0;
  for (const limite of DOUGLAS_M) {
    if (alturaM >= limite) grau++;
  }
  return grau;
}

/**
 * Altura de um ponto: a medida do nó quando existe, senão a estimativa do RMS
 * vertical. A segunda componente diz qual das duas foi.
 */
export function alturaDoPonto(p: ImuLatest): { m: number; fonte: "medida" | "estimada" } {
  if (p.wave_height_m != null) return { m: p.wave_height_m, fonte: "medida" };
  const semRuido = Math.sqrt(Math.max(0, p.vertical_accel_rms ** 2 - RUIDO_SENSOR_MS2 ** 2));
  const fator = (PERIODO_TIPICO_S / (2 * Math.PI)) ** 2;
  return { m: 4 * semRuido * fator, fonte: "estimada" };
}

function mediana(valores: number[]): number | null {
  if (valores.length === 0) return null;
  const ordenados = [...valores].sort((a, b) => a - b);
  const meio = Math.floor(ordenados.length / 2);
  return ordenados.length % 2 === 1
    ? ordenados[meio]!
    : (ordenados[meio - 1]! + ordenados[meio]!) / 2;
}

export function estadoMar(imu: ImuPoint[]): EstadoMar {
  const recentes = imu.slice(-JANELAS_MEDIANA);
  if (recentes.length === 0) return VAZIO;

  // Mediana só sobre uma proveniência: misturar medição com estimativa dava
  // um número sem pedigree. Havendo alguma janela medida, é essa que conta.
  const medidas = recentes.filter((p) => p.wave_height_m != null);
  const fonte = medidas.length > 0 ? ("medida" as const) : ("estimada" as const);
  const base = fonte === "medida" ? medidas : recentes;

  const alturaM = mediana(base.map((p) => alturaDoPonto(p).m));
  const periodoS =
    fonte === "medida"
      ? mediana(medidas.flatMap((p) => (p.wave_period_s != null ? [p.wave_period_s] : [])))
      : null;
  const swellM =
    fonte === "medida"
      ? mediana(medidas.flatMap((p) => (p.swell_height_m != null ? [p.swell_height_m] : [])))
      : null;

  const grau = alturaM === null ? null : grauDouglas(alturaM);
  return {
    alturaM,
    periodoS,
    swellM,
    grau,
    rotulo: grau === null ? null : ROTULO_MAR[grau]!,
    fonte,
  };
}
